import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useToast } from "@flat-ui";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import {
  updateProjectDetails,
  updateSnapshotDetails,
} from "@store/projects/projects-slice-thunk";
import { useAppDispatch } from "@store/store-helper";
import {
  ChangeProjectDetailsEvents,
  SnapshotEvents,
} from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useCallback } from "react";
import { AccessLevelEntityType } from "@components/access-level/access-level-utils";
import { capitalizeFirstLetter } from "@utils/string-utils";

/** Hook to return handler for changing the access level */
export function useOnAccessLevelChange(type: AccessLevelEntityType): {
  onAccessLevelChange: (
    value: FaroCoreApiTypes.ProjectAccessLevel,
    location: string,
    entityId?: FaroCoreApiTypes.ProjectId | FaroCoreApiTypes.SnapshotId
  ) => Promise<void> | void;
} {
  const dispatch = useAppDispatch();
  const { openToast } = useToast();
  const { trackEvent } = useTrackEvent();
  const { handleErrorWithToast } = useErrorContext();

  /** Change access level. Currently it is only used for project */
  const onAccessLevelChange = useCallback(
    async (
      value: FaroCoreApiTypes.ProjectAccessLevel,
      location: string,
      entityId?: FaroCoreApiTypes.ProjectId | FaroCoreApiTypes.SnapshotId
    ) => {
      trackEvent({
        name:
          type === "project"
            ? ChangeProjectDetailsEvents.changeProjectAccessLevel
            : SnapshotEvents.editSnapshot,
        props: { accessLevel: value, location },
      });

      try {
        if (type === "project") {
          await dispatch(
            updateProjectDetails({
              projectId: entityId,
              payload: {
                accessLevel: value,
              },
            })
          ).unwrap();
        }

        if (type === "snapshot" && entityId) {
          await dispatch(
            updateSnapshotDetails({
              snapshotId: entityId,
              payload: { accessLevel: value },
            })
          ).unwrap();
        }

        openToast({
          title: `${capitalizeFirstLetter(
            type
          )} access level changed successfully.`,
        });
      } catch (error) {
        // Handle error directly here in order to show specific info about the field that failed: access level
        // Otherwise the error will be handled by the error slice, but that will show a generic message without details
        handleErrorWithToast({
          id: `useOnAccessLevelChange-${Date.now().toString()}`,
          title: `Could not change ${type} access level`,
          error,
        });
      }
    },
    [dispatch, handleErrorWithToast, openToast, trackEvent, type]
  );

  return { onAccessLevelChange };
}
